import { useContext } from 'react';

import { IEnrollmentPremiumDto } from '@modules/members/enrollments/common/types';
import { EnrollmentContext } from '@modules/members/enrollments/context/MemberMyEnrollmentProvider';

const useValidateSubsidyAmount = () => {
    const { enableContinueButton, enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const { setValue } = enrollmentPremiumFormContext;

    const isValidSubsidy = (premium, ehbPercent) => {
        if (ehbPercent) {
            if (premium.grossPremiumAmount * ehbPercent < premium.stateSubsidyAmount) {
                return false;
            }
        } else {
            if (premium.grossPremiumAmount < premium.stateSubsidyAmount) {
                return false;
            }
        }

        if (premium.maxStateSubsidy !== undefined && premium.stateSubsidyAmount > premium.maxStateSubsidy) {
            return false;
        }

        if (
            premium.maxStateSubsidy2 !== undefined &&
            premium.maxStateSubsidy2 !== null &&
            (premium.stateSubsidy2Amount ?? 0) > premium.maxStateSubsidy2
        ) {
            return false;
        }

        return true;
    };

    return (premiumData: IEnrollmentPremiumDto) => {
        const premium = { ...premiumData };
        enableContinueButton();
        if (isValidSubsidy(premium, selectedEnrollment.ehbPercent)) {
            premium.netPremiumAmount = premium.grossPremiumAmount;
            premium.minimumPremium = premium.minimumPremium || 0;
            if (premium.aptcAmount !== undefined) {
                if (premium.netPremiumAmount - premium.aptcAmount < premium.minimumPremium) {
                    premium.aptcAmount = premium.grossPremiumAmount - premium.minimumPremium;
                    premium.netPremiumAmount = premium.minimumPremium;
                } else {
                    premium.netPremiumAmount = premium.netPremiumAmount - premium.aptcAmount;
                }
            }

            if (premium.stateSubsidyAmount !== undefined) {
                premium.netPremiumAmount =
                    premium.netPremiumAmount - ((premium.stateSubsidyAmount || 0) + (premium.stateSubsidy2Amount || 0));
            }
        } else {
            premium.netPremiumAmount =
                premium.grossPremiumAmount -
                ((premium.stateSubsidyAmount || 0) + (premium.aptcAmount || 0) + (premium.stateSubsidy2Amount || 0));
        }

        if (premium.statePremiumCreditAmt !== undefined) {
            if (premium.netPremiumAmount - premium.statePremiumCreditAmt < 0) {
                premium.netPremiumAmount = 0;
            } else {
                premium.netPremiumAmount = premium.netPremiumAmount - premium.statePremiumCreditAmt;
            }
        }

        if (premium?.isMedicaidTransitionEligible) {
            let medicaidTransitionAmount =
                premium.grossPremiumAmount -
                (premium.aptcAmount + (premium.stateSubsidy2Amount || 0) + premium.stateSubsidyAmount) -
                (premium.maxAptc - premium.aptcAmount);
            premium.medicaidTransitionAmount = medicaidTransitionAmount < 0 ? 0 : medicaidTransitionAmount;
            setValue(
                `enrollmentPremiumDtoList.${premium.month - 1}.medicaidTransitionAmount`,
                premium.medicaidTransitionAmount,
                {
                    shouldDirty: true,
                    shouldValidate: true,
                },
            );
        }

        premium.netPremiumAmount = Number(premium.netPremiumAmount.toFixed(2)) || 0;
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.netPremiumAmount`, premium.netPremiumAmount, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue();
    };
};

export default useValidateSubsidyAmount;
